import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Scale, Calendar, Clock, Video, Check, X, MessageSquare } from "lucide-react";
import { formatDate } from "@/lib/format";
import logoMark from "@/assets/logo-mark.png";

const PROVIDER_LABEL = {
  meet: "Google Meet", zoom: "Zoom", teams: "Microsoft Teams", webex: "Webex", outro: "Videoconferência",
};

export default function ConfirmationPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const r = await http.get(`/public/confirm/${token}`);
      setInfo(r.data);
    } catch (e) {
      setError({ expired: e?.response?.status === 410, msg: e?.response?.data?.detail || "Link inválido" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const submit = async (choice) => {
    setSubmitting(true);
    try {
      await http.post(`/public/confirm/${token}`, { choice, note: note || null });
      toast.success(choice === "confirmed" ? "Presença confirmada!" : choice === "cannot" ? "Aviso enviado" : "Pedido de remarcação enviado");
      load();
    } catch (e) {
      if (e?.response?.status === 410) { load(); return; }
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Scale className="w-6 h-6 animate-pulse text-stone-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="max-w-md w-full text-center bg-white border border-stone-200 rounded-md p-8">
          <X className="w-10 h-10 mx-auto text-rose-brand mb-3" strokeWidth={1.5} />
          <h1 data-testid="link-erro-titulo" className="font-display text-xl font-bold text-stone-900 mb-2">{error.expired ? "Este link expirou" : "Link inválido"}</h1>
          <p data-testid="link-erro-texto" className="text-sm text-stone-500">{error.expired ? "Para tratar deste compromisso, fale diretamente com o escritório." : error.msg}</p>
        </div>
      </div>
    );
  }

  const h = info.hearing || {};
  const alreadyResponded = Boolean(info.client_response);
  const testemunha = info.papel === "testemunha";      // Lote 176: convite de testemunha usa a mesma página

  return (
    <div className="min-h-screen bg-stone-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {info.escritorio ? (
          // Lote 167 — o cliente vê o escritório (logo opcional + nome); o IApauta fica discreto no rodapé.
          <div className="flex items-center gap-3 mb-8 justify-center" data-testid="confirmacao-escritorio">
            {info.escritorio.logo_url ? <img src={info.escritorio.logo_url} alt="" className="h-11 w-auto max-w-[160px] object-contain" data-testid="confirmacao-escritorio-logo" /> : null}
            <span className="font-display font-bold text-stone-900 text-xl text-center break-words min-w-0" data-testid="confirmacao-escritorio-nome">{info.escritorio.nome}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-8 justify-center">
            <img src={logoMark} alt="" className="w-8 h-8 rounded-md" />
            <span className="font-display font-bold text-stone-900 text-lg"><span className="text-gold-600">IA</span>pauta</span>
          </div>
        )}

        <div className="bg-white border border-stone-200 rounded-md p-8">
          <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-2">
            {/* "reuniao" virou "Outro" na tela (Lote 56) — "de compromisso" fica mais
                natural aqui do que "de outro". */}
            {testemunha ? "Convite para depor como testemunha"
              : `Convite ${h.event_type === "reuniao" ? "de compromisso" : h.event_type === "prazo" ? "de prazo" : "de audiência"}`}
          </div>
          <h1 className="font-display text-2xl font-bold text-stone-900 mb-2">
            {info.client_name ? `Olá, ${info.client_name}` : "Você tem um compromisso"}
          </h1>
          <p className="text-sm text-stone-500 mb-6">
            {testemunha ? "Confirme sua presença ou avise que não poderá comparecer. Vamos avisar o advogado."
              : "Confirme sua presença ou peça uma remarcação. Vamos avisar seu advogado(a)."}
          </p>

          <div className="space-y-3 mb-6">
            <Row icon={Calendar} label="Data" value={formatDate(h.date)} />
            <Row icon={Clock} label="Horário" value={h.time} />
            {h.court && <Row label="Vara / Comarca" value={h.court} />}
            {h.case_number && <Row label="Processo" value={h.case_number} />}
            {h.description && <Row label="Descrição" value={h.description} />}
          </div>

          {h.is_virtual && h.meeting_url && (
            <div className="bg-emerald-100 border border-emerald-200 rounded-md p-4 mb-6">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm mb-2">
                <Video className="w-4 h-4" strokeWidth={2} />
                {PROVIDER_LABEL[h.meeting_provider] || "Videoconferência"}
              </div>
              <a
                data-testid="join-meeting-button"
                href={h.meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-emerald-brand text-white text-sm font-semibold px-4 py-2 rounded-md"
              >
                Entrar na videochamada
              </a>
              {h.meeting_password && (
                <p className="text-xs text-emerald-700 mt-2">
                  Senha: <strong className="tabular-nums">{h.meeting_password}</strong>
                </p>
              )}
            </div>
          )}

          {alreadyResponded ? (
            <div className="bg-stone-100 border border-stone-200 rounded-md p-4 text-center">
              <Check className="w-6 h-6 mx-auto text-emerald-brand mb-1" strokeWidth={2} />
              <p className="text-sm font-semibold text-stone-900">
                {info.client_response === "confirmed" ? "Presença confirmada" : info.client_response === "cannot" ? "Aviso registrado: você não poderá comparecer" : "Remarcação solicitada"}
              </p>
              <p className="text-xs text-stone-500 mt-1">
                Registrado em {new Date(info.client_responded_at).toLocaleString("pt-BR")}
              </p>
            </div>
          ) : (
            <>
              <textarea
                data-testid="confirmation-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Deixe uma observação (opcional)"
                rows={2}
                className="w-full text-sm p-3 rounded-md border border-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-brand mb-4"
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="confirm-attendance-button"
                  onClick={() => submit("confirmed")}
                  disabled={submitting}
                  className="flex-1 bg-emerald-brand text-white gap-2"
                >
                  <Check className="w-4 h-4" strokeWidth={2} /> Confirmar presença
                </Button>
                <Button
                  data-testid="reschedule-button"
                  onClick={() => submit(testemunha ? "cannot" : "reschedule")}
                  disabled={submitting}
                  variant="outline"
                  className="flex-1 border-stone-300 text-stone-700 gap-2"
                >
                  <MessageSquare className="w-4 h-4" strokeWidth={2} /> {testemunha ? "Não poderei comparecer" : "Pedir remarcação"}
                </Button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-stone-400 mt-6">
          Recebeu por engano? Basta ignorar este link.
        </p>
        {info.escritorio && (
          <p className="text-center text-[11px] text-stone-400 mt-2" data-testid="confirmacao-via">Enviado via IApauta</p>
        )}
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    {Icon && <Icon className="w-4 h-4 text-stone-400 mt-0.5 shrink-0" strokeWidth={1.5} />}
    <div className="flex-1 flex justify-between gap-4">
      <span className="text-xs uppercase tracking-widest text-stone-500 mt-0.5">{label}</span>
      <span className="text-sm text-stone-900 font-medium text-right">{value || "—"}</span>
    </div>
  </div>
);
